import type { DataSource } from '../../types/health';
import { DataSourceLabel } from '../ui/DataSourceLabel';
import './LineChart.css';

export type LineChartPoint = {
  date: string;
  value: number;
  unit: string;
  source: DataSource;
};

export function LineChart({ title, points }: { title: string; points: LineChartPoint[] }) {
  const width = 720;
  const height = 300;
  const padding = { top: 42, right: 32, bottom: 44, left: 36 };
  const chartId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const values = points.map((point) => point.value);
  const min = values.length ? Math.min(...values) - 0.4 : 0;
  const max = values.length ? Math.max(...values) + 0.4 : 1;
  const range = max - min || 1;
  const last = points.at(-1);

  const coordinates = points.map((point, index) => {
    const x = padding.left + (index / Math.max(points.length - 1, 1)) * (width - padding.left - padding.right);
    const y = height - padding.bottom - ((point.value - min) / range) * (height - padding.top - padding.bottom);
    return { ...point, x, y };
  });

  const path = coordinates.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

  function labelX(point: (typeof coordinates)[number], index: number) {
    if (index === 0) return point.x + 28;
    if (index === coordinates.length - 1) return point.x - 28;
    return point.x;
  }

  function labelAnchor(index: number) {
    if (index === 0) return 'start';
    if (index === coordinates.length - 1) return 'end';
    return 'middle';
  }

  function labelY(point: (typeof coordinates)[number]) {
    return point.y < padding.top + 18 ? point.y + 24 : point.y - 14;
  }

  function formatDate(date: string) {
    return date.slice(5).replace('-', '/');
  }

  return (
    <figure className="line-chart" aria-label={title}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-labelledby={`${chartId}-title ${chartId}-desc`}>
        <title id={`${chartId}-title`}>{title}</title>
        <desc id={`${chartId}-desc`}>
          Latest value {last?.value} {last?.unit} from {last?.source}.
        </desc>
        {[0, 1, 2].map((line) => {
          const y = padding.top + line * ((height - padding.top - padding.bottom) / 2);
          return <line key={line} x1={padding.left} y1={y} x2={width - padding.right} y2={y} className="chart-grid-line" />;
        })}
        {path ? <path d={path} className="chart-line" /> : null}
        {coordinates.map((point, index) => (
          <g key={point.date} className="chart-point-group">
            <circle cx={point.x} cy={point.y} r="4" className="chart-point">
              <title>{`${point.date}: ${point.value} ${point.unit} (${point.source})`}</title>
            </circle>
            <text x={labelX(point, index)} y={labelY(point)} textAnchor={labelAnchor(index)} className="chart-point-label">
              <tspan x={labelX(point, index)}>{formatDate(point.date)}</tspan>
              <tspan x={labelX(point, index)} dy="13">
                {point.value} {point.unit}
              </tspan>
            </text>
          </g>
        ))}
      </svg>
      {last ? (
        <figcaption>
          <span className="numeric">
            Latest: {last.value} {last.unit} on {last.date}
          </span>
          <DataSourceLabel source={last.source} />
        </figcaption>
      ) : null}
      <ol className="chart-data-list" aria-label={`${title} data points`}>
        {points.map((point) => (
          <li key={point.date}>
            <span>{point.date}</span>
            <strong className="numeric">
              {point.value} {point.unit}
            </strong>
            <DataSourceLabel source={point.source} />
          </li>
        ))}
      </ol>
    </figure>
  );
}
